import React, { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { mockActionItems, mockCommittees, mockMeetings, mockUsers, mockLists, mockListActionItems } from '../mock/data';
import { 
  Search, Filter, Plus, Upload, FileText, Clock, CheckCircle2, 
  AlertCircle, XCircle, ChevronDown, ChevronRight, Settings,
  History, Paperclip, Sparkles, X, AlertTriangle, MessageSquare,
  MoreVertical, Edit, Trash2, ArrowUpRight, Layers, List, Users, FolderOpen,
  ListTodo, Briefcase, LayoutDashboard
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useSidebar } from '../context/SidebarContext';
import HeaderGlow from '../components/HeaderGlow';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

// Format date as "DD Mmm YYYY"
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Status configuration
const getStatusConfig = (status) => {
  switch (status) {
    case 'completed':
    case 'closed':
      return { label: 'Completed', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle2 };
    case 'in-progress':
      return { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Clock };
    case 'late':
      return { label: 'Late', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle };
    case 'on-hold':
      return { label: 'On Hold', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: XCircle };
    default:
      return { label: 'Open', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Clock };
  }
};

const ActionItems = ({ 
  viewMode = 'all',  // 'all', 'collective', 'lists' - use directly from props
  showManageMeetingsPage = false,
  showManageListsPage = false,
  showMOMPage = false,
  showDashboard = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const { collapsed, setCollapsed } = useSidebar();
  const { currentColorTheme } = useTheme();
  
  // Dashboard filters
  const [dashboardFilter, setDashboardFilter] = useState({ 
    type: 'all', // 'all', 'meetings', 'lists'
    selectedMeeting: 'all',
    selectedList: 'all',
    selectedAssignee: 'all'
  });
  
  // Filter states
  const [selectedMeetingTypes, setSelectedMeetingTypes] = useState([]);
  const [selectedMeetingDates, setSelectedMeetingDates] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedEscalatedTo, setSelectedEscalatedTo] = useState([]);
  const [selectedLists, setSelectedLists] = useState([]);
  
  // Collapsible filter sections
  const [filterSections, setFilterSections] = useState({
    meetingType: true,
    meetingDate: true,
    listType: true,
    status: true,
    assigned: false,
    escalated: false
  });
  
  // Group by state
  const [groupBy, setGroupBy] = useState('none');
  
  // Dialog states
  const [showAddActionItem, setShowAddActionItem] = useState(false);
  const [showUpdateHistory, setShowUpdateHistory] = useState(null);
  const [showAddUpdate, setShowAddUpdate] = useState(null);
  const [showUploadMOM, setShowUploadMOM] = useState(false);
  
  // MOM Files state
  const [momFiles, setMomFiles] = useState([
    { id: 'mom-1', meetingId: 'EXCOMM-012', fileName: 'EXCOMM_Nov2025.pdf', uploadDate: '2025-11-21', uploadedBy: '1' },
    { id: 'mom-2', meetingId: 'FIN-003', fileName: 'Finance_Q3.pdf', uploadDate: '2025-09-26', uploadedBy: '2' },
  ]);
  
  // Local state for data - Meeting action items (collective)
  const [meetingActionItems, setMeetingActionItems] = useState(mockActionItems);
  // List action items
  const [listActionItems, setListActionItems] = useState(mockListActionItems || []);
  const [committees, setCommittees] = useState(mockCommittees);
  const [lists, setLists] = useState(mockLists || [
    { id: 'chairman', name: 'Chairman Action Items', type: 'list' },
    { id: 'ceo', name: 'CEO Action Items', type: 'list' },
    { id: 'company-a', name: 'Company A Action Items', type: 'list' },
    { id: 'company-b', name: 'Company B Action Items', type: 'list' }
  ]);
  
  // New action item form
  const [newActionItem, setNewActionItem] = useState({
    isCollective: true, // true = Collective (Board, ExComm), false = List
    meetingType: '',
    listType: '',
    meetingDate: '',
    description: '',
    assignedTo: '',
    dueDate: '',
    status: 'open'
  });
  
  // New update form
  const [newUpdate, setNewUpdate] = useState({ text: '', documents: [] });
  
  // New meeting type/list form
  const [newMeeting, setNewMeeting] = useState({ name: '', code: '' });
  const [newList, setNewList] = useState({ name: '' });

  // Get unique meeting dates from meetings
  const uniqueMeetingDates = useMemo(() => {
    const dates = [...new Set(mockMeetings.map(m => m.date))].sort().reverse();
    return dates;
  }, []);

  // Combined action items based on view mode
  const allActionItems = useMemo(() => {
    if (viewMode === 'collective') {
      // Only meeting-based items
      return meetingActionItems.map(item => ({ ...item, itemType: 'meeting' }));
    }
    if (viewMode === 'lists') {
      // Only list-based items
      return listActionItems;
    }
    // All items combined
    const meetingItems = meetingActionItems.map(item => ({ ...item, itemType: 'meeting' }));
    return [...meetingItems, ...listActionItems];
  }, [viewMode, meetingActionItems, listActionItems]);

  // Filter action items
  const filteredActionItems = useMemo(() => {
    return allActionItems.filter(item => {
      const meeting = item.meetingId ? mockMeetings.find(m => m.id === item.meetingId) : null;
      const committee = meeting ? committees.find(c => c.id === meeting.committeeId) : null;
      const list = item.listId ? lists.find(l => l.id === item.listId) : null;
      const assignee = mockUsers.find(u => u.id === item.assignedTo);
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesDescription = item.description.toLowerCase().includes(query);
        const matchesMeeting = item.meetingId?.toLowerCase().includes(query);
        const matchesList = list?.name.toLowerCase().includes(query);
        const matchesAssignee = assignee?.name.toLowerCase().includes(query);
        if (!matchesDescription && !matchesMeeting && !matchesList && !matchesAssignee) return false;
      }
      
      // Meeting Type filter (for meeting items)
      if (selectedMeetingTypes.length > 0 && item.itemType === 'meeting' && committee) {
        if (!selectedMeetingTypes.includes(committee.id)) return false;
      }
      
      // Meeting Date filter (for meeting items)
      if (selectedMeetingDates.length > 0 && item.itemType === 'meeting' && meeting) {
        if (!selectedMeetingDates.includes(meeting.date)) return false;
      }
      
      // List filter (for list items)
      if (selectedLists.length > 0 && item.itemType === 'list') {
        if (!selectedLists.includes(item.listId)) return false;
      }
      
      // Status filter
      if (selectedStatuses.length > 0) {
        if (!selectedStatuses.includes(item.status)) return false;
      }
      
      // Assignee filter
      if (selectedAssignees.length > 0) {
        if (!selectedAssignees.includes(item.assignedTo)) return false;
      }
      
      // Escalated To filter
      if (selectedEscalatedTo.length > 0) {
        if (!item.escalatedTo || !selectedEscalatedTo.includes(item.escalatedTo)) return false;
      }
      
      return true;
    });
  }, [allActionItems, searchQuery, selectedMeetingTypes, selectedMeetingDates, selectedStatuses, selectedAssignees, selectedEscalatedTo, selectedLists, committees, lists]);

  // Group items
  const groupedItems = useMemo(() => {
    if (groupBy === 'none') return { 'All Items': filteredActionItems };
    
    const grouped = {};
    filteredActionItems.forEach(item => {
      let key = 'Other';
      if (groupBy === 'meeting') {
        const meeting = mockMeetings.find(m => m.id === item.meetingId);
        const committee = meeting ? committees.find(c => c.id === meeting.committeeId) : null;
        key = committee?.name || meeting?.title || item.meetingId || 'Unknown';
      } else if (groupBy === 'status') {
        key = getStatusConfig(item.status).label;
      } else if (groupBy === 'assignee') {
        const assignee = mockUsers.find(u => u.id === item.assignedTo);
        key = assignee?.name || 'Unassigned';
      }
      
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    });
    return grouped;
  }, [filteredActionItems, groupBy, committees]);

  // Handle adding new action item
  const handleAddActionItem = () => {
    let meetingId = '';
    if (newActionItem.isCollective) {
      const committee = committees.find(c => c.id === newActionItem.meetingType);
      meetingId = `${committee?.shortCode || 'MTG'}-${newActionItem.meetingDate.replace(/-/g, '')}`;
    }
    
    const newItem = {
      id: `ai-${Date.now()}`,
      meetingId: newActionItem.isCollective ? meetingId : null,
      listId: newActionItem.isCollective ? null : newActionItem.listType,
      description: newActionItem.description,
      assignedTo: newActionItem.assignedTo,
      dueDate: newActionItem.dueDate,
      status: 'open',
      currentUpdate: '',
      updateDate: new Date().toISOString().split('T')[0],
      escalatedTo: null,
      escalationReason: null,
      escalatedBy: null,
      createdAt: new Date().toISOString().split('T')[0],
      updates: []
    };
    setActionItems([...actionItems, newItem]);
    setNewActionItem({
      isCollective: true,
      meetingType: '',
      listType: '',
      meetingDate: '',
      description: '',
      assignedTo: '',
      dueDate: '',
      status: 'open'
    });
    setShowAddActionItem(false);
  };

  // Handle adding update
  const handleAddUpdate = (actionItemId) => {
    setActionItems(actionItems.map(item => {
      if (item.id === actionItemId) {
        const update = {
          id: `upd-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          text: newUpdate.text,
          userId: '1',
          documents: newUpdate.documents
        };
        return {
          ...item,
          currentUpdate: newUpdate.text,
          updateDate: update.date,
          updates: [...item.updates, update]
        };
      }
      return item;
    }));
    setNewUpdate({ text: '', documents: [] });
    setShowAddUpdate(null);
  };

  // Handle escalate
  const handleEscalate = (actionItemId, escalateTo, reason) => {
    setActionItems(actionItems.map(item => {
      if (item.id === actionItemId) {
        return { ...item, escalatedTo: escalateTo, escalationReason: reason, escalatedBy: '1' };
      }
      return item;
    }));
  };

  // Handle delete
  const handleDelete = (actionItemId) => {
    if (confirm('Are you sure you want to delete this action item?')) {
      setActionItems(actionItems.filter(item => item.id !== actionItemId));
    }
  };

  // Handle adding new meeting type
  const handleAddMeeting = () => {
    if (!newMeeting.name || !newMeeting.code) return;
    const newCommittee = {
      id: newMeeting.code.toLowerCase(),
      name: newMeeting.name,
      code: newMeeting.code,
      description: ''
    };
    setCommittees([...committees, newCommittee]);
    setNewMeeting({ name: '', code: '' });
  };

  // Handle adding new list
  const handleAddList = () => {
    if (!newList.name) return;
    const newListItem = {
      id: newList.name.toLowerCase().replace(/\s+/g, '-'),
      name: newList.name,
      type: 'list'
    };
    setLists([...lists, newListItem]);
    setNewList({ name: '' });
  };

  const toggleFilterSection = (section) => {
    setFilterSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Get page title and subtitle based on view mode
  const getPageTitle = () => {
    if (showDashboard) return { title: 'Action Items Dashboard', subtitle: 'Overview of all action items and metrics' };
    if (showMOMPage) return { title: 'Minutes of Meeting (MOM)', subtitle: 'View and manage uploaded MOM files' };
    if (showManageMeetingsPage) return { title: 'Manage Meetings', subtitle: 'Add, edit or remove meeting types' };
    if (showManageListsPage) return { title: 'Manage Lists', subtitle: 'Add, edit or remove action item lists' };
    if (viewMode === 'collective') return { title: 'Meeting Action Items', subtitle: 'Action items from Board, Executive Committee, and other meetings' };
    if (viewMode === 'lists') return { title: 'List Action Items', subtitle: 'Action items from Chairman, CEO, and company-specific lists' };
    return { title: 'All Action Items', subtitle: 'Track and manage action items from meetings and lists' };
  };
  const pageInfo = getPageTitle();

  // Dashboard statistics
  const dashboardStats = useMemo(() => {
    // Filter items based on dashboard filter
    let items = [...meetingActionItems.map(i => ({...i, itemType: 'meeting'})), ...listActionItems];
    
    if (dashboardFilter.type === 'meetings') {
      items = meetingActionItems.map(i => ({...i, itemType: 'meeting'}));
      if (dashboardFilter.selectedMeeting !== 'all') {
        items = items.filter(item => {
          const meeting = mockMeetings.find(m => m.id === item.meetingId);
          return meeting?.committeeId === dashboardFilter.selectedMeeting;
        });
      }
    } else if (dashboardFilter.type === 'lists') {
      items = listActionItems;
      if (dashboardFilter.selectedList !== 'all') {
        items = items.filter(item => item.listId === dashboardFilter.selectedList);
      }
    }
    
    if (dashboardFilter.selectedAssignee !== 'all') {
      items = items.filter(item => item.assignedTo === dashboardFilter.selectedAssignee);
    }

    const total = items.length;
    const completed = items.filter(i => i.status === 'completed').length;
    const inProgress = items.filter(i => i.status === 'in-progress').length;
    const late = items.filter(i => i.status === 'late').length;
    const onHold = items.filter(i => i.status === 'on-hold').length;
    const escalated = items.filter(i => i.escalatedTo).length;
    
    // By meeting type
    const byMeetingType = committees.map(c => ({
      name: c.name,
      code: c.code,
      count: meetingActionItems.filter(i => {
        const meeting = mockMeetings.find(m => m.id === i.meetingId);
        return meeting?.committeeId === c.id;
      }).length
    }));
    
    // By list
    const byList = lists.map(l => ({
      name: l.name,
      count: listActionItems.filter(i => i.listId === l.id).length
    }));
    
    // By assignee
    const byAssignee = mockUsers.map(u => ({
      name: u.name,
      count: items.filter(i => i.assignedTo === u.id).length
    }));

    return { total, completed, inProgress, late, onHold, escalated, byMeetingType, byList, byAssignee };
  }, [meetingActionItems, listActionItems, dashboardFilter, committees, lists]);

  // Render Dashboard
  if (showDashboard) {
    return (
      <TooltipProvider>
        <div className="flex h-full">
          <div className="flex-1 overflow-y-auto">
            {/* Header */}
            <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-amber-500 via-orange-500 to-red-500'} p-8`}>
              <div className="flex items-end justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white">{pageInfo.title}</h1>
                  <p className="text-white/80">{pageInfo.subtitle}</p>
                </div>
              </div>
            </div>
            <HeaderGlow />

            {/* Dashboard Content */}
            <div className="p-6 space-y-6">
              {/* Filters Row */}
              <div className="flex flex-wrap gap-4 p-4 bg-card border border-border rounded-lg">
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-muted-foreground">View:</Label>
                  <Select value={dashboardFilter.type} onValueChange={(v) => setDashboardFilter({...dashboardFilter, type: v, selectedMeeting: 'all', selectedList: 'all'})}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Items</SelectItem>
                      <SelectItem value="meetings">Meetings Only</SelectItem>
                      <SelectItem value="lists">Lists Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {dashboardFilter.type === 'meetings' && (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground">Meeting:</Label>
                    <Select value={dashboardFilter.selectedMeeting} onValueChange={(v) => setDashboardFilter({...dashboardFilter, selectedMeeting: v})}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Meetings</SelectItem>
                        {committees.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {dashboardFilter.type === 'lists' && (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground">List:</Label>
                    <Select value={dashboardFilter.selectedList} onValueChange={(v) => setDashboardFilter({...dashboardFilter, selectedList: v})}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Lists</SelectItem>
                        {lists.map(l => (
                          <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-muted-foreground">Assignee:</Label>
                  <Select value={dashboardFilter.selectedAssignee} onValueChange={(v) => setDashboardFilter({...dashboardFilter, selectedAssignee: v})}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Assignees</SelectItem>
                      {mockUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className={`p-4 rounded-xl border ${currentColorTheme?.cardBorderClass || 'border-border'} bg-card`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Total Items</span>
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-blue-500 to-blue-600'}`}>
                      <ListTodo className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-foreground">{dashboardStats.total}</p>
                </div>
                
                <div className={`p-4 rounded-xl border ${currentColorTheme?.cardBorderClass || 'border-border'} bg-card`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Completed</span>
                    <div className="p-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-green-500">{dashboardStats.completed}</p>
                </div>
                
                <div className={`p-4 rounded-xl border ${currentColorTheme?.cardBorderClass || 'border-border'} bg-card`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">In Progress</span>
                    <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-600">
                      <Clock className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-blue-500">{dashboardStats.inProgress}</p>
                </div>
                
                <div className={`p-4 rounded-xl border ${currentColorTheme?.cardBorderClass || 'border-border'} bg-card`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Late</span>
                    <div className="p-2 rounded-lg bg-gradient-to-r from-red-500 to-rose-600">
                      <AlertCircle className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-red-500">{dashboardStats.late}</p>
                </div>
                
                <div className={`p-4 rounded-xl border ${currentColorTheme?.cardBorderClass || 'border-border'} bg-card`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">On Hold</span>
                    <div className="p-2 rounded-lg bg-gradient-to-r from-yellow-500 to-amber-600">
                      <XCircle className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-yellow-500">{dashboardStats.onHold}</p>
                </div>
                
                <div className={`p-4 rounded-xl border ${currentColorTheme?.cardBorderClass || 'border-border'} bg-card`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Escalated</span>
                    <div className="p-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700">
                      <AlertTriangle className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-red-600">{dashboardStats.escalated}</p>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* By Meeting Type */}
                <div className={`p-6 rounded-xl border ${currentColorTheme?.cardBorderClass || 'border-border'} bg-card`}>
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    By Meeting Type
                  </h3>
                  <div className="space-y-3">
                    {dashboardStats.byMeetingType.map((item, index) => {
                      const maxCount = Math.max(...dashboardStats.byMeetingType.map(i => i.count), 1);
                      const percentage = (item.count / maxCount) * 100;
                      const colors = ['from-violet-500 to-purple-600', 'from-blue-500 to-cyan-600', 'from-emerald-500 to-green-600', 'from-amber-500 to-orange-600', 'from-rose-500 to-pink-600', 'from-indigo-500 to-blue-600'];
                      return (
                        <div key={item.code} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground">{item.name}</span>
                            <span className="text-muted-foreground">{item.count} items</span>
                          </div>
                          <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                            <div 
                              className={`h-full bg-gradient-to-r ${colors[index % colors.length]} rounded-full transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* By List */}
                <div className={`p-6 rounded-xl border ${currentColorTheme?.cardBorderClass || 'border-border'} bg-card`}>
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    By List
                  </h3>
                  <div className="space-y-3">
                    {dashboardStats.byList.map((item, index) => {
                      const maxCount = Math.max(...dashboardStats.byList.map(i => i.count), 1);
                      const percentage = (item.count / maxCount) * 100;
                      const colors = ['from-amber-500 to-orange-600', 'from-rose-500 to-pink-600', 'from-cyan-500 to-blue-600'];
                      return (
                        <div key={item.name} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-foreground">{item.name}</span>
                            <span className="text-muted-foreground">{item.count} items</span>
                          </div>
                          <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                            <div 
                              className={`h-full bg-gradient-to-r ${colors[index % colors.length]} rounded-full transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Status Distribution & By Assignee */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Status Distribution - Pie-like visualization */}
                <div className={`p-6 rounded-xl border ${currentColorTheme?.cardBorderClass || 'border-border'} bg-card`}>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Status Distribution</h3>
                  <div className="flex items-center justify-center">
                    <div className="relative w-48 h-48">
                      {/* Circular progress rings */}
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="96" cy="96" r="80" stroke="currentColor" strokeWidth="16" fill="none" className="text-muted/20" />
                        <circle 
                          cx="96" cy="96" r="80" 
                          stroke="url(#greenGradient)" 
                          strokeWidth="16" 
                          fill="none" 
                          strokeDasharray={`${(dashboardStats.completed / dashboardStats.total) * 502} 502`}
                          className="transition-all duration-500"
                        />
                        <circle 
                          cx="96" cy="96" r="60" 
                          stroke="url(#blueGradient)" 
                          strokeWidth="14" 
                          fill="none" 
                          strokeDasharray={`${(dashboardStats.inProgress / dashboardStats.total) * 377} 377`}
                          className="transition-all duration-500"
                        />
                        <circle 
                          cx="96" cy="96" r="42" 
                          stroke="url(#redGradient)" 
                          strokeWidth="12" 
                          fill="none" 
                          strokeDasharray={`${(dashboardStats.late / dashboardStats.total) * 264} 264`}
                          className="transition-all duration-500"
                        />
                        <defs>
                          <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#22c55e" />
                            <stop offset="100%" stopColor="#10b981" />
                          </linearGradient>
                          <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="100%" stopColor="#06b6d4" />
                          </linearGradient>
                          <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#ef4444" />
                            <stop offset="100%" stopColor="#f43f5e" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-3xl font-bold text-foreground">{dashboardStats.total}</span>
                        <span className="text-xs text-muted-foreground">Total</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-500" />
                      <span className="text-sm text-muted-foreground">Completed</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500" />
                      <span className="text-sm text-muted-foreground">In Progress</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-red-500 to-rose-500" />
                      <span className="text-sm text-muted-foreground">Late</span>
                    </div>
                  </div>
                </div>

                {/* By Assignee */}
                <div className={`p-6 rounded-xl border ${currentColorTheme?.cardBorderClass || 'border-border'} bg-card`}>
                  <h3 className="text-lg font-semibold text-foreground mb-4">By Assignee</h3>
                  <div className="space-y-3">
                    {dashboardStats.byAssignee.filter(a => a.count > 0).map((item, index) => {
                      const maxCount = Math.max(...dashboardStats.byAssignee.map(i => i.count), 1);
                      const percentage = (item.count / maxCount) * 100;
                      return (
                        <div key={item.name} className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs bg-gradient-to-r from-violet-500 to-purple-600 text-white">
                              {item.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-foreground">{item.name}</span>
                              <span className="text-muted-foreground">{item.count}</span>
                            </div>
                            <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
                              <div 
                                className={`h-full bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-violet-500 to-purple-600'} rounded-full transition-all duration-500`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // Render Manage Meetings Page
  if (showManageMeetingsPage) {
    return (
      <TooltipProvider>
        <div className="flex h-full">
          <div className="flex-1 overflow-y-auto">
            {/* Header */}
            <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-amber-500 via-orange-500 to-red-500'} p-8`}>
              <div className="flex items-end justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white">{pageInfo.title}</h1>
                  <p className="text-white/80">{pageInfo.subtitle}</p>
                </div>
                <div className="text-right mr-4">
                  <p className="text-4xl font-bold text-white">{committees.length}</p>
                  <p className="text-white/80 text-sm">Meeting Types</p>
                </div>
              </div>
            </div>
            <HeaderGlow />

            {/* Content */}
            <div className="p-6">
              {/* Add New Meeting Type Form */}
              <div className="mb-6 p-4 bg-card border border-border rounded-lg">
                <h3 className="text-sm font-medium text-foreground mb-3">Add New Meeting Type</h3>
                <div className="flex gap-3">
                  <Input
                    placeholder="Meeting name (e.g., Audit Committee)"
                    value={newMeeting.name}
                    onChange={(e) => setNewMeeting({ ...newMeeting, name: e.target.value })}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Code (e.g., AUDIT)"
                    value={newMeeting.code}
                    onChange={(e) => setNewMeeting({ ...newMeeting, code: e.target.value.toUpperCase() })}
                    className="w-32"
                  />
                  <Button onClick={handleAddMeeting} className={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'}`}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Meeting Types Table */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="flex items-center px-4 py-3 bg-muted/30 border-b border-border text-sm font-medium text-muted-foreground gap-4">
                  <div className="w-[200px] flex-shrink-0">Meeting Name</div>
                  <div className="w-[100px] flex-shrink-0">Code</div>
                  <div className="flex-1">Description</div>
                  <div className="w-[100px] flex-shrink-0 text-center">Items</div>
                  <div className="w-[100px] flex-shrink-0 text-center">Actions</div>
                </div>
                <div className="divide-y divide-border">
                  {committees.map(committee => {
                    const itemCount = meetingActionItems.filter(ai => {
                      const meeting = mockMeetings.find(m => m.id === ai.meetingId);
                      return meeting?.committeeId === committee.id;
                    }).length;
                    return (
                      <div key={committee.id} className="flex items-center px-4 py-4 hover:bg-muted/20 transition-colors gap-4">
                        <div className="w-[200px] flex-shrink-0">
                          <p className="text-sm font-medium text-foreground">{committee.name}</p>
                        </div>
                        <div className="w-[100px] flex-shrink-0">
                          <Badge variant="outline" className="text-xs">{committee.code}</Badge>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-muted-foreground">{committee.description || 'No description'}</p>
                        </div>
                        <div className="w-[100px] flex-shrink-0 text-center">
                          <Badge className="bg-primary/20 text-primary">{itemCount} items</Badge>
                        </div>
                        <div className="w-[100px] flex-shrink-0 flex justify-center gap-2">
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // Render Manage Lists Page
  if (showManageListsPage) {
    return (
      <TooltipProvider>
        <div className="flex h-full">
          <div className="flex-1 overflow-y-auto">
            {/* Header */}
            <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-amber-500 via-orange-500 to-red-500'} p-8`}>
              <div className="flex items-end justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white">{pageInfo.title}</h1>
                  <p className="text-white/80">{pageInfo.subtitle}</p>
                </div>
                <div className="text-right mr-4">
                  <p className="text-4xl font-bold text-white">{lists.length}</p>
                  <p className="text-white/80 text-sm">List Types</p>
                </div>
              </div>
            </div>
            <HeaderGlow />

            {/* Content */}
            <div className="p-6">
              {/* Add New List Form */}
              <div className="mb-6 p-4 bg-card border border-border rounded-lg">
                <h3 className="text-sm font-medium text-foreground mb-3">Add New List</h3>
                <div className="flex gap-3">
                  <Input
                    placeholder="List name (e.g., CFO Action Items)"
                    value={newList.name}
                    onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                    className="flex-1"
                  />
                  <Button onClick={handleAddList} className={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'}`}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Lists Table */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="flex items-center px-4 py-3 bg-muted/30 border-b border-border text-sm font-medium text-muted-foreground gap-4">
                  <div className="flex-1">List Name</div>
                  <div className="w-[100px] flex-shrink-0 text-center">Items</div>
                  <div className="w-[100px] flex-shrink-0 text-center">Actions</div>
                </div>
                <div className="divide-y divide-border">
                  {lists.map(list => {
                    const itemCount = listActionItems.filter(ai => ai.listId === list.id).length;
                    return (
                      <div key={list.id} className="flex items-center px-4 py-4 hover:bg-muted/20 transition-colors gap-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">{list.name}</p>
                        </div>
                        <div className="w-[100px] flex-shrink-0 text-center">
                          <Badge className="bg-primary/20 text-primary">{itemCount} items</Badge>
                        </div>
                        <div className="w-[100px] flex-shrink-0 flex justify-center gap-2">
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // Render MOM Files Page
  if (showMOMPage) {
    return (
      <TooltipProvider>
        <div className="flex h-full">
          <div className="flex-1 overflow-y-auto">
            {/* Header */}
            <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-amber-500 via-orange-500 to-red-500'} p-8`}>
              <div className="flex items-end justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-white">{pageInfo.title}</h1>
                  <p className="text-white/80">{pageInfo.subtitle}</p>
                </div>
                <div className="text-right mr-4">
                  <p className="text-4xl font-bold text-white">{momFiles.length}</p>
                  <p className="text-white/80 text-sm">Total Files</p>
                </div>
              </div>
            </div>
            <HeaderGlow />

            {/* Toolbar */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search MOM files..."
                  className="pl-9"
                />
              </div>
              <Button 
                size="sm"
                className={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'} text-white`}
                onClick={() => setShowUploadMOM(true)}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload MOM
              </Button>
            </div>

            {/* MOM Files Table */}
            <div className="p-4">
              <div className="flex items-center px-4 py-3 bg-muted/30 border-b border-border text-sm font-medium text-muted-foreground gap-4 rounded-t-lg">
                <div className="w-[200px] flex-shrink-0">Meeting</div>
                <div className="flex-1">File Name</div>
                <div className="w-[120px] flex-shrink-0">Upload Date</div>
                <div className="w-[120px] flex-shrink-0">Uploaded By</div>
                <div className="w-[100px] flex-shrink-0 text-center">Actions</div>
              </div>
              <div className="divide-y divide-border border border-t-0 border-border rounded-b-lg">
                {momFiles.map(file => {
                  const meeting = mockMeetings.find(m => m.id === file.meetingId);
                  const committee = meeting ? committees.find(c => c.id === meeting.committeeId) : null;
                  const uploader = mockUsers.find(u => u.id === file.uploadedBy);
                  return (
                    <div key={file.id} className="flex items-center px-4 py-4 hover:bg-muted/20 transition-colors gap-4">
                      <div className="w-[200px] flex-shrink-0">
                        <p className="text-sm font-medium text-foreground">{committee?.name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(meeting?.date)}</p>
                      </div>
                      <div className="flex-1 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-foreground">{file.fileName}</span>
                      </div>
                      <div className="w-[120px] flex-shrink-0">
                        <span className="text-sm text-muted-foreground">{formatDate(file.uploadDate)}</span>
                      </div>
                      <div className="w-[120px] flex-shrink-0">
                        <span className="text-sm text-foreground">{uploader?.name || 'Unknown'}</span>
                      </div>
                      <div className="w-[100px] flex-shrink-0 flex justify-center gap-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Sparkles className="w-4 h-4 text-amber-500" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {momFiles.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                    <FileText className="w-12 h-12 mb-4 opacity-50" />
                    <p>No MOM files uploaded yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Upload MOM Dialog */}
          <Dialog open={showUploadMOM} onOpenChange={setShowUploadMOM}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Upload Minutes of Meeting (MOM)</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Meeting Type</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Select meeting type" />
                    </SelectTrigger>
                    <SelectContent>
                      {committees.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Meeting Date</Label>
                  <Input type="date" />
                </div>
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-sm text-foreground mb-1">Drag & drop your MOM file here</p>
                  <p className="text-xs text-muted-foreground">PDF, DOC, DOCX supported</p>
                  <Button variant="outline" className="mt-4">Browse Files</Button>
                </div>
                <div className="bg-muted/30 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    <span className="text-sm font-medium">AI Extract Action Items</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Use AI to automatically extract action items from the MOM.</p>
                  <Button variant="outline" className="w-full">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Extract with AI
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowUploadMOM(false)}>Cancel</Button>
                <Button>Upload MOM</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex h-full">
        {/* Filter Panel */}
        {showFilters && (
          <div className="w-64 border-r border-border bg-card/50 p-4 space-y-4 overflow-y-auto flex-shrink-0">
            {/* Filter Header with Close Button */}
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filters
              </h3>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6"
                onClick={() => setShowFilters(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-background"
              />
            </div>

            {/* Show Meeting filters only for 'all' and 'collective' views */}
            {(viewMode === 'all' || viewMode === 'collective') && (
              <>
                {/* Meeting Type Filter - Collapsible */}
                <Collapsible open={filterSections.meetingType} onOpenChange={() => toggleFilterSection('meetingType')}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                    Meeting Type
                    {filterSections.meetingType ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 pt-2">
                    {committees.map(committee => (
                      <div key={committee.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`mt-${committee.id}`}
                          checked={selectedMeetingTypes.includes(committee.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedMeetingTypes([...selectedMeetingTypes, committee.id]);
                            } else {
                              setSelectedMeetingTypes(selectedMeetingTypes.filter(c => c !== committee.id));
                            }
                          }}
                        />
                        <label htmlFor={`mt-${committee.id}`} className="text-sm text-foreground cursor-pointer">
                          {committee.name}
                        </label>
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>

                {/* Meeting Date Filter - Collapsible List */}
                <Collapsible open={filterSections.meetingDate} onOpenChange={() => toggleFilterSection('meetingDate')}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                    Meeting Date
                    {filterSections.meetingDate ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 pt-2 max-h-40 overflow-y-auto">
                    {uniqueMeetingDates.map(date => (
                      <div key={date} className="flex items-center gap-2">
                        <Checkbox
                          id={`date-${date}`}
                          checked={selectedMeetingDates.includes(date)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedMeetingDates([...selectedMeetingDates, date]);
                            } else {
                              setSelectedMeetingDates(selectedMeetingDates.filter(d => d !== date));
                            }
                          }}
                        />
                        <label htmlFor={`date-${date}`} className="text-sm text-foreground cursor-pointer">
                          {formatDate(date)}
                        </label>
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              </>
            )}

            {/* Show List filter only for 'all' and 'lists' views */}
            {(viewMode === 'all' || viewMode === 'lists') && (
              <Collapsible open={filterSections.listType} onOpenChange={() => toggleFilterSection('listType')}>
                <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                  List Type
                  {filterSections.listType ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1 pt-2">
                  {lists.map(list => (
                    <div key={list.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`list-${list.id}`}
                        checked={selectedLists.includes(list.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedLists([...selectedLists, list.id]);
                          } else {
                            setSelectedLists(selectedLists.filter(l => l !== list.id));
                          }
                        }}
                      />
                      <label htmlFor={`list-${list.id}`} className="text-sm text-foreground cursor-pointer">
                        {list.name}
                      </label>
                    </div>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Status Filter - Collapsible */}
            <Collapsible open={filterSections.status} onOpenChange={() => toggleFilterSection('status')}>
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                Status
                {filterSections.status ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 pt-2">
                {['late', 'in-progress', 'completed', 'closed', 'on-hold'].map(status => (
                  <div key={status} className="flex items-center gap-2">
                    <Checkbox
                      id={`status-${status}`}
                      checked={selectedStatuses.includes(status)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedStatuses([...selectedStatuses, status]);
                        } else {
                          setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                        }
                      }}
                    />
                    <label htmlFor={`status-${status}`} className="text-sm text-foreground cursor-pointer capitalize">
                      {status.replace('-', ' ')}
                    </label>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>

            {/* Assigned Filter - Collapsible */}
            <Collapsible open={filterSections.assigned} onOpenChange={() => toggleFilterSection('assigned')}>
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                Assigned
                {filterSections.assigned ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 pt-2">
                {mockUsers.map(user => (
                  <div key={user.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`assignee-${user.id}`}
                      checked={selectedAssignees.includes(user.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedAssignees([...selectedAssignees, user.id]);
                        } else {
                          setSelectedAssignees(selectedAssignees.filter(a => a !== user.id));
                        }
                      }}
                    />
                    <label htmlFor={`assignee-${user.id}`} className="text-sm text-foreground cursor-pointer">
                      {user.name}
                    </label>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>

            {/* Escalated To Filter - Collapsible */}
            <Collapsible open={filterSections.escalated} onOpenChange={() => toggleFilterSection('escalated')}>
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                Escalated To
                {filterSections.escalated ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 pt-2">
                {mockUsers.map(user => (
                  <div key={user.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`escalated-${user.id}`}
                      checked={selectedEscalatedTo.includes(user.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedEscalatedTo([...selectedEscalatedTo, user.id]);
                        } else {
                          setSelectedEscalatedTo(selectedEscalatedTo.filter(e => e !== user.id));
                        }
                      }}
                    />
                    <label htmlFor={`escalated-${user.id}`} className="text-sm text-foreground cursor-pointer">
                      {user.name}
                    </label>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Header */}
          <div className={`bg-gradient-to-r ${currentColorTheme?.headerGradient || 'from-amber-500 via-orange-500 to-red-500'} p-8`}>
            <div className="flex items-end justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">{pageInfo.title}</h1>
                <p className="text-white/80">{pageInfo.subtitle}</p>
              </div>
              <div className="text-right mr-4">
                <p className="text-4xl font-bold text-white">{filteredActionItems.length}</p>
                <p className="text-white/80 text-sm">Total Items</p>
              </div>
            </div>
          </div>
          <HeaderGlow />

          {/* Toolbar */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setShowFilters(!showFilters);
                  if (!showFilters && !collapsed) {
                    setCollapsed(true);
                  }
                }}
                className={showFilters ? 'bg-primary/10 border-primary' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search action items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Group By */}
              <div className="flex items-center gap-2 ml-4">
                <span className="text-sm text-muted-foreground">Group by:</span>
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger className="w-[140px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                    <SelectItem value="assignee">Assignee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowUploadMOM(true)}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload MOM
              </Button>
              <Button 
                size="sm"
                className={`bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'} text-white`}
                onClick={() => setShowAddActionItem(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Action Item
              </Button>
            </div>
          </div>

          {/* Table */}
          {Object.entries(groupedItems).map(([groupName, items]) => (
            <div key={groupName}>
              {/* Group Header */}
              {groupBy !== 'none' && (
                <div className="px-4 py-2 bg-muted/50 border-b border-border">
                  <span className="text-sm font-semibold text-foreground">{groupName}</span>
                  <Badge variant="outline" className="ml-2">{items.length}</Badge>
                </div>
              )}

              {/* Table Header */}
              <div className="flex items-center px-4 py-3 bg-muted/30 border-b border-border text-sm font-medium text-muted-foreground gap-4">
                <div className="w-[140px] flex-shrink-0">{viewMode === 'lists' ? 'List' : viewMode === 'collective' ? 'Meeting' : 'Source'}</div>
                <div className="flex-1 min-w-[200px]">Action Item</div>
                <div className="w-[120px] flex-shrink-0">Assigned</div>
                <div className="w-[140px] flex-shrink-0">Status / Due</div>
                <div className="flex-1 min-w-[200px]">Update</div>
                <div className="w-[100px] flex-shrink-0">Update Date</div>
                <div className="w-[60px] flex-shrink-0 text-center">Escalated</div>
                <div className="w-[50px] flex-shrink-0 text-center">Actions</div>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-border">
                {items.map((item) => {
                  const meeting = item.meetingId ? mockMeetings.find(m => m.id === item.meetingId) : null;
                  const committee = meeting ? committees.find(c => c.id === meeting.committeeId) : null;
                  const listItem = item.listId ? lists.find(l => l.id === item.listId) : null;
                  const assignee = mockUsers.find(u => u.id === item.assignedTo);
                  const escalatedToUser = item.escalatedTo ? mockUsers.find(u => u.id === item.escalatedTo) : null;
                  const escalatedByUser = item.escalatedBy ? mockUsers.find(u => u.id === item.escalatedBy) : null;
                  const statusConfig = getStatusConfig(item.status);
                  const StatusIcon = statusConfig.icon;
                  
                  // Determine source name based on item type
                  const isListItem = item.itemType === 'list' || item.listId;
                  const sourceName = isListItem ? listItem?.name : (committee?.name || meeting?.title || 'Unknown');
                  const sourceDate = isListItem ? item.createdAt : meeting?.date;

                  return (
                    <div 
                      key={item.id} 
                      className="flex items-start px-4 py-4 hover:bg-muted/20 transition-colors gap-4"
                    >
                      {/* Source (Meeting or List) */}
                      <div className="w-[140px] flex-shrink-0">
                        <p className="text-sm font-medium text-foreground">
                          {sourceName || '-'}
                        </p>
                        {sourceDate && (
                          <p className="text-xs text-muted-foreground">{formatDate(sourceDate)}</p>
                        )}
                      </div>

                      {/* Action Item */}
                      <div className="flex-1 min-w-[200px]">
                        <p className="text-sm text-foreground">{item.description}</p>
                      </div>

                      {/* Assigned */}
                      <div className="w-[120px] flex-shrink-0">
                        {assignee && (
                          <span className="text-sm text-foreground">{assignee.name}</span>
                        )}
                      </div>

                      {/* Status / Due Date */}
                      <div className="w-[140px] flex-shrink-0">
                        <Badge className={`${statusConfig.color} border whitespace-nowrap mb-1`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                        {item.dueDate && (
                          <p className="text-xs text-muted-foreground">Due: {formatDate(item.dueDate)}</p>
                        )}
                      </div>

                      {/* Update */}
                      <div className="flex-1 min-w-[200px]">
                        <p className="text-sm text-muted-foreground">{item.currentUpdate || '-'}</p>
                      </div>

                      {/* Update Date */}
                      <div className="w-[100px] flex-shrink-0">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(item.updates?.[item.updates.length - 1]?.date || item.createdAt)}
                        </span>
                      </div>

                      {/* Escalated */}
                      <div className="w-[60px] flex-shrink-0 flex justify-center">
                        {escalatedToUser ? (
                          <Tooltip>
                            <TooltipTrigger>
                              <AlertTriangle className="w-5 h-5 text-red-500 cursor-pointer" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <div className="space-y-1">
                                <p className="text-sm font-medium">Escalated To: {escalatedToUser.name}</p>
                                {escalatedByUser && (
                                  <p className="text-sm text-muted-foreground">Escalated By: {escalatedByUser.name}</p>
                                )}
                                <p className="text-sm text-muted-foreground">Reason: {item.escalationReason || 'No reason'}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="w-[50px] flex-shrink-0 flex justify-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setShowAddUpdate(item.id)}>
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Add Comment
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setShowUpdateHistory(item)}>
                              <History className="w-4 h-4 mr-2" />
                              View History ({item.updates?.length || 0})
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              const escalateTo = prompt('Escalate to (user name):');
                              const reason = prompt('Escalation reason:');
                              if (escalateTo && reason) {
                                const user = mockUsers.find(u => u.name.toLowerCase().includes(escalateTo.toLowerCase()));
                                if (user) handleEscalate(item.id, user.id, reason);
                              }
                            }}>
                              <ArrowUpRight className="w-4 h-4 mr-2" />
                              Escalate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(item.id)}>
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {filteredActionItems.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <FileText className="w-12 h-12 mb-4 opacity-50" />
              <p>No action items found</p>
              <p className="text-sm">Try adjusting your filters or add a new action item</p>
            </div>
          )}
        </div>

        {/* Add Action Item Dialog */}
        <Dialog open={showAddActionItem} onOpenChange={setShowAddActionItem}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Action Item</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Meetings vs List Toggle */}
              <div className="flex items-center justify-center p-1 bg-muted/30 rounded-lg">
                <button
                  type="button"
                  onClick={() => setNewActionItem({...newActionItem, isCollective: true})}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    newActionItem.isCollective 
                      ? `bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'} text-white shadow-md`
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Layers className="w-4 h-4" />
                  Meetings
                </button>
                <button
                  type="button"
                  onClick={() => setNewActionItem({...newActionItem, isCollective: false})}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    !newActionItem.isCollective 
                      ? `bg-gradient-to-r ${currentColorTheme?.progressGradient || 'from-primary to-primary'} text-white shadow-md`
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <List className="w-4 h-4" />
                  Lists
                </button>
              </div>

              {newActionItem.isCollective ? (
                <>
                  <div className="space-y-2">
                    <Label>Meeting Type</Label>
                    <Select value={newActionItem.meetingType} onValueChange={(v) => setNewActionItem({...newActionItem, meetingType: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select meeting type" />
                      </SelectTrigger>
                      <SelectContent>
                        {committees.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Meeting Date</Label>
                    <Input 
                      type="date"
                      value={newActionItem.meetingDate}
                      onChange={(e) => setNewActionItem({...newActionItem, meetingDate: e.target.value})}
                      className="bg-background text-foreground [color-scheme:dark]"
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label>List</Label>
                  <Select value={newActionItem.listType} onValueChange={(v) => setNewActionItem({...newActionItem, listType: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select list" />
                    </SelectTrigger>
                    <SelectContent>
                      {lists.map(l => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea 
                  placeholder="Action item description..."
                  value={newActionItem.description}
                  onChange={(e) => setNewActionItem({...newActionItem, description: e.target.value})}
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Assigned To</Label>
                  <Select value={newActionItem.assignedTo} onValueChange={(v) => setNewActionItem({...newActionItem, assignedTo: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select assignee" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockUsers.map(user => (
                        <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input 
                    type="date"
                    value={newActionItem.dueDate}
                    onChange={(e) => setNewActionItem({...newActionItem, dueDate: e.target.value})}
                    className="bg-background text-foreground [color-scheme:dark]"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddActionItem(false)}>Cancel</Button>
              <Button onClick={handleAddActionItem}>Add Action Item</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Update Dialog */}
        <Dialog open={!!showAddUpdate} onOpenChange={() => setShowAddUpdate(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Comment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Comment</Label>
                <Textarea 
                  placeholder="Enter your comment..."
                  value={newUpdate.text}
                  onChange={(e) => setNewUpdate({...newUpdate, text: e.target.value})}
                  rows={4}
                />
              </div>
              <div className="space-y-2">
                <Label>Attach Documents (Optional)</Label>
                <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                  <Paperclip className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Drag & drop or click to upload</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddUpdate(null)}>Cancel</Button>
              <Button onClick={() => handleAddUpdate(showAddUpdate)}>Add Comment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update History Dialog */}
        <Dialog open={!!showUpdateHistory} onOpenChange={() => setShowUpdateHistory(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Update History</DialogTitle>
            </DialogHeader>
            {showUpdateHistory && (
              <div className="space-y-4 py-4">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <p className="text-sm text-foreground">{showUpdateHistory.description}</p>
                </div>
                
                <div className="space-y-4">
                  {(!showUpdateHistory.updates || showUpdateHistory.updates.length === 0) ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No updates yet</p>
                  ) : (
                    showUpdateHistory.updates.map((update) => {
                      const user = mockUsers.find(u => u.id === update.userId);
                      return (
                        <div key={update.id} className="border-l-2 border-primary/30 pl-4 pb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Avatar className="w-6 h-6">
                              <AvatarImage src={user?.avatar} />
                              <AvatarFallback>{user?.name?.charAt(0) || '?'}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium text-foreground">{user?.name || 'Unknown'}</span>
                            <span className="text-xs text-muted-foreground">{formatDate(update.date)}</span>
                          </div>
                          <p className="text-sm text-foreground">{update.text}</p>
                          {update.documents && update.documents.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {update.documents.map((doc, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  <Paperclip className="w-3 h-3 mr-1" />
                                  {doc}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Upload MOM Dialog */}
        <Dialog open={showUploadMOM} onOpenChange={setShowUploadMOM}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Upload Minutes of Meeting (MOM)</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Meeting Type</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select meeting type" />
                  </SelectTrigger>
                  <SelectContent>
                    {committees.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Meeting Date</Label>
                <Input type="date" />
              </div>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-foreground mb-1">Drag & drop your MOM file here</p>
                <p className="text-xs text-muted-foreground">PDF, DOC, DOCX supported</p>
                <Button variant="outline" className="mt-4">Browse Files</Button>
              </div>
              <div className="bg-muted/30 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium">AI Extract Action Items</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Use AI to automatically extract action items from the MOM.</p>
                <Button variant="outline" className="w-full">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Extract with AI
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadMOM(false)}>Cancel</Button>
              <Button>Upload MOM</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default ActionItems;
